import { BannerElement, BannerSize, DataFeed } from '../types';
import { exportToHTML } from './exportUtils';

interface HTMLCSStoImageConfig {
  userId: string;
  apiKey: string;
  baseUrl: string;
}

interface CloudflareConfig {
  accountEmail: string;
  apiKey: string;
  zoneId: string;
  accountId: string;
  r2BucketName: string;
}

interface DeploymentResult {
  success: boolean;
  htmlUrl: string;
  imageUrl: string;
  error?: string;
  deploymentId: string;
}

// Configuration for HTMLCSStoImage.com - Fixed API endpoint
let HTMLCSSTOIMAGE_CONFIG: HTMLCSStoImageConfig = {
  userId: '44be8eac-3bdb-4c64-aa86-675a75e6d3d9',
  apiKey: 'd8985858-4e6a-46de-bcde-a443cc6cb4b6',
  baseUrl: 'https://hcti.io/v1'
};

// Load credentials from localStorage on startup
const loadStoredCredentials = () => {
  try {
    const stored = localStorage.getItem('dealercentives_htmlcss_credentials');
    if (stored) {
      const credentials = JSON.parse(stored);
      HTMLCSSTOIMAGE_CONFIG = {
        ...HTMLCSSTOIMAGE_CONFIG,
        userId: credentials.userId || HTMLCSSTOIMAGE_CONFIG.userId,
        apiKey: credentials.apiKey || HTMLCSSTOIMAGE_CONFIG.apiKey
      };
    }
  } catch (error) {
    console.warn('Failed to load stored API credentials:', error);
  }
};

// Call loadStoredCredentials on module initialization
loadStoredCredentials();

// API Credentials Management Functions
export const getHTMLCSStoImageCredentials = () => {
  return {
    userId: HTMLCSSTOIMAGE_CONFIG.userId,
    apiKey: HTMLCSSTOIMAGE_CONFIG.apiKey
  };
};

export const updateHTMLCSStoImageCredentials = (userId: string, apiKey: string): boolean => {
  try {
    // Validate inputs
    if (!userId.trim() || !apiKey.trim()) {
      throw new Error('User ID and API Key are required');
    }

    // Update configuration
    HTMLCSSTOIMAGE_CONFIG.userId = userId.trim();
    HTMLCSSTOIMAGE_CONFIG.apiKey = apiKey.trim();

    // Save to localStorage
    localStorage.setItem('dealercentives_htmlcss_credentials', JSON.stringify({
      userId: HTMLCSSTOIMAGE_CONFIG.userId,
      apiKey: HTMLCSSTOIMAGE_CONFIG.apiKey,
      updatedAt: new Date().toISOString()
    }));

    return true;
  } catch (error) {
    console.error('Failed to update API credentials:', error);
    return false;
  }
};

export const resetHTMLCSStoImageCredentials = () => {
  // Reset to default values
  HTMLCSSTOIMAGE_CONFIG.userId = '44be8eac-3bdb-4c64-aa86-675a75e6d3d9';
  HTMLCSSTOIMAGE_CONFIG.apiKey = 'd8985858-4e6a-46de-bcde-a443cc6cb4b6';
  
  // Remove from localStorage
  localStorage.removeItem('dealercentives_htmlcss_credentials');
};

// Cloudflare configuration - Load from localStorage
let CLOUDFLARE_CONFIG: CloudflareConfig = {
  accountEmail: 'support@dealercentive.com',
  apiKey: 'Bearer 7aa03210fd5b9a2f9f033d3ecc63f5033d50b',
  zoneId: '0083446677b3cbc6207ee6db04b0599e',
  accountId: 'beb860d7a22688e729c77f7e8feab88a',
  r2BucketName: 'dealercentive-banners'
};

// Load Cloudflare credentials
const loadCloudflareCredentials = () => {
  try {
    const stored = localStorage.getItem('dealercentives_cloudflare_credentials');
    if (stored) {
      const credentials = JSON.parse(stored);
      CLOUDFLARE_CONFIG = { ...CLOUDFLARE_CONFIG, ...credentials };
    }
  } catch (error) {
    console.warn('Failed to load Cloudflare credentials:', error);
  }
};

// Initialize Cloudflare credentials
loadCloudflareCredentials();

// Cloudflare credentials management
export const getCloudflareCredentials = () => {
  return {
    accountEmail: CLOUDFLARE_CONFIG.accountEmail,
    apiKey: CLOUDFLARE_CONFIG.apiKey ? '••••••••••••••••' : '',
    zoneId: CLOUDFLARE_CONFIG.zoneId,
    r2BucketName: CLOUDFLARE_CONFIG.r2BucketName
  };
};

export const updateCloudflareCredentials = (credentials: Partial<CloudflareConfig>): boolean => {
  try {
    CLOUDFLARE_CONFIG = { ...CLOUDFLARE_CONFIG, ...credentials };
    
    localStorage.setItem('dealercentives_cloudflare_credentials', JSON.stringify({
      ...CLOUDFLARE_CONFIG,
      updatedAt: new Date().toISOString()
    }));

    return true;
  } catch (error) {
    console.error('Failed to update Cloudflare credentials:', error);
    return false;
  }
};

// Real deployment to Cloudflare R2 with HTMLCSStoImage.com
export const deployBannerToProduction = async (
  elements: BannerElement[],
  size: BannerSize,
  dataFeedArray: DataFeed[],
  config: {
    vehicleType: string;
    priority: number;
    isActive: boolean;
    deploymentType: 'html' | 'image';
    imageFormat: 'png' | 'jpeg';
    refreshInterval: number;
  },
  clientId: string
): Promise<DeploymentResult> => {
  try {
    const deploymentId = generateDeploymentId();
    const primaryDataFeed = dataFeedArray[0];
    
    // Generate HTML content
    const htmlContent = exportToHTML(elements, size, primaryDataFeed, true);
    const enhancedHTML = addProductionMetadata(htmlContent, {
      clientId,
      vehicleType: config.vehicleType,
      priority: config.priority,
      deploymentId,
      refreshInterval: config.refreshInterval,
      lastUpdated: new Date().toISOString()
    });

    // Deploy HTML to Cloudflare R2
    const htmlKey = generateHtmlKey(clientId, config.vehicleType, size.name, config.priority);
    const htmlUrl = await deployHtmlToR2(enhancedHTML, htmlKey);

    let imageUrl = '';
    
    if (config.deploymentType === 'image') {
      // For image deployment, the worker will generate images on-demand
      // We just need to provide the image URL format
      imageUrl = generateImageUrl(clientId, config.vehicleType, size.name, config.priority, config.imageFormat);
    }

    return {
      success: true,
      htmlUrl,
      imageUrl,
      deploymentId
    };

  } catch (error) {
    console.error('Production deployment failed:', error);
    return {
      success: false,
      htmlUrl: '',
      imageUrl: '',
      error: error instanceof Error ? error.message : 'Unknown deployment error',
      deploymentId: ''
    };
  }
};

// Test HTMLCSStoImage API connectivity with proper credentials
export const testHTMLCSStoImageAPI = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('Testing HTMLCSStoImage API connectivity...');
    console.log('API URL:', `${HTMLCSSTOIMAGE_CONFIG.baseUrl}/image`);
    console.log('User ID:', HTMLCSSTOIMAGE_CONFIG.userId);
    console.log('API Key:', HTMLCSSTOIMAGE_CONFIG.apiKey ? '••••••••••••••••' : 'Not set');
    
    // Create proper Basic Auth header
    const authString = `${HTMLCSSTOIMAGE_CONFIG.userId}:${HTMLCSSTOIMAGE_CONFIG.apiKey}`;
    const encodedAuth = btoa(authString);
    
    // Use a minimal test request to verify API credentials
    const testResponse = await fetch(`${HTMLCSSTOIMAGE_CONFIG.baseUrl}/image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Basic ${encodedAuth}`,
        'User-Agent': 'DealerCentives/1.0'
      },
      body: JSON.stringify({
        html: '<div style="width:100px;height:100px;background:#007bff;color:white;display:flex;align-items:center;justify-content:center;font-family:Arial;">TEST</div>',
        viewport_width: 100,
        viewport_height: 100,
        format: 'png'
      }),
      signal: AbortSignal.timeout(15000) // 15 second timeout for health check
    });

    console.log('Health check response status:', testResponse.status);

    if (testResponse.status === 401) {
      return { success: false, error: 'Invalid API credentials - please check your User ID and API Key' };
    } else if (testResponse.status === 404) {
      return { success: false, error: 'API endpoint not found - the service URL may be incorrect' };
    } else if (testResponse.status === 429) {
      return { success: false, error: 'API rate limit exceeded' };
    } else if (testResponse.status === 403) {
      return { success: false, error: 'API access forbidden - check account status' };
    } else if (testResponse.ok) {
      // API is working
      const result = await testResponse.json();
      if (result.url) {
        return { success: true };
      }
      return { success: false, error: 'Invalid response format from API' };
    }
    
    return { success: false, error: `API test failed with status ${testResponse.status}` };
  } catch (error) {
    console.error('HTMLCSStoImage API test failed:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error during API test'
    };
  }
};

// Deploy HTML content to Cloudflare R2 - REAL IMPLEMENTATION
const deployHtmlToR2 = async (htmlContent: string, key: string): Promise<string> => {
  try {
    // Create the R2 API URL
    const r2ApiUrl = `https://api.cloudflare.com/client/v4/accounts/${CLOUDFLARE_CONFIG.accountId}/r2/buckets/${CLOUDFLARE_CONFIG.r2BucketName}/objects/${encodeURIComponent(key)}`;
    
    // Upload the HTML content to R2
    const response = await fetch(r2ApiUrl, {
      method: 'PUT',
      headers: {
        'Authorization': CLOUDFLARE_CONFIG.apiKey,
        'Content-Type': 'text/html',
        'x-amz-meta-content-type': 'text/html',
        'x-amz-meta-cache-control': 'public, max-age=3600'
      },
      body: htmlContent
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to upload to R2: ${response.status} ${response.statusText} - ${errorText}`);
    }

    // Return the public URL for the uploaded file
    const r2Url = `https://banners.dealercentive.us/${key}`;
    console.log(`HTML deployed to: ${r2Url}`);
    
    return r2Url;
  } catch (error) {
    console.error('R2 deployment error:', error);
    throw new Error(`Failed to deploy HTML to R2: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Generate image URL for worker
const generateImageUrl = (clientId: string, vehicleType: string, sizeName: string, priority: number, format: string): string => {
  const sanitizedClientId = clientId.toLowerCase().replace(/[^a-z0-9]/g, '');
  const sanitizedVehicleType = vehicleType.toLowerCase().replace(/[^a-z0-9]/g, '');
  const sanitizedSize = sizeName.toLowerCase().replace(/[^a-z0-9]/g, '');
  
  return `https://images.dealercentive.us/${sanitizedClientId}/${sanitizedVehicleType}/${sanitizedSize}-${priority}.${format}`;
};

// Add production metadata to HTML
const addProductionMetadata = (
  baseHTML: string,
  metadata: {
    clientId: string;
    vehicleType: string;
    priority: number;
    deploymentId: string;
    refreshInterval: number;
    lastUpdated: string;
  }
): string => {
  const enhancements = `
    <!-- DealerCentives Production Deployment -->
    <meta name="dc-client-id" content="${metadata.clientId}">
    <meta name="dc-vehicle-type" content="${metadata.vehicleType}">
    <meta name="dc-priority" content="${metadata.priority}">
    <meta name="dc-deployment-id" content="${metadata.deploymentId}">
    <meta name="dc-refresh-interval" content="${metadata.refreshInterval}">
    <meta name="dc-last-updated" content="${metadata.lastUpdated}">
    <meta name="dc-version" content="1.0.0">
    
    <!-- Production optimizations -->
    <style>
      /* Optimize for HTMLCSStoImage.com rendering */
      * {
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        image-rendering: -webkit-optimize-contrast;
        image-rendering: crisp-edges;
      }
      
      body {
        margin: 0;
        padding: 0;
        background: white;
      }
      
      .banner-container {
        position: relative;
        overflow: hidden;
      }
      
      /* Ensure images load properly */
      img {
        max-width: 100%;
        height: auto;
        display: block;
      }
    </style>
    
    <!-- Analytics and tracking -->
    <script>
      // Production analytics
      if (typeof gtag !== 'undefined') {
        gtag('event', 'banner_view', {
          'client_id': '${metadata.clientId}',
          'vehicle_type': '${metadata.vehicleType}',
          'deployment_id': '${metadata.deploymentId}'
        });
      }
    </script>
  `;
  
  return baseHTML.replace('</head>', `${enhancements}\n</head>`);
};

// Utility functions
const generateDeploymentId = (): string => {
  return `dep_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const generateHtmlKey = (clientId: string, vehicleType: string, sizeName: string, priority: number): string => {
  const sanitizedClientId = clientId.toLowerCase().replace(/[^a-z0-9]/g, '');
  const sanitizedVehicleType = vehicleType.toLowerCase().replace(/[^a-z0-9]/g, '');
  const sanitizedSize = sizeName.toLowerCase().replace(/[^a-z0-9]/g, '');
  
  return `${sanitizedClientId}/${sanitizedVehicleType}/${sanitizedSize}-${priority}.html`;
};

// Batch deployment for multiple banners
export const batchDeployToProduction = async (
  deployments: Array<{
    elements: BannerElement[];
    size: BannerSize;
    dataFeedArray: DataFeed[];
    config: {
      vehicleType: string;
      priority: number;
      isActive: boolean;
      deploymentType: 'html' | 'image';
      imageFormat: 'png' | 'jpeg';
      refreshInterval: number;
    };
    clientId: string;
  }>
): Promise<Array<DeploymentResult & { sizeId: string }>> => {
  const results: Array<DeploymentResult & { sizeId: string }> = [];
  
  // Deploy sequentially to avoid overwhelming the browser
  for (const deployment of deployments) {
    try {
      const result = await deployBannerToProduction(
        deployment.elements,
        deployment.size,
        deployment.dataFeedArray,
        deployment.config,
        deployment.clientId
      );
      
      results.push({
        ...result,
        sizeId: deployment.size.id
      });
    } catch (error) {
      results.push({
        success: false,
        htmlUrl: '',
        imageUrl: '',
        error: error instanceof Error ? error.message : 'Deployment failed',
        deploymentId: '',
        sizeId: deployment.size.id
      });
    }
    
    // Add a small delay between deployments to avoid browser freezing
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  return results;
};

// Enhanced health check for production deployment with better error handling
export const checkProductionHealth = async (): Promise<{
  status: 'healthy' | 'degraded' | 'down';
  services: Array<{
    name: string;
    status: 'up' | 'down' | 'unknown';
    responseTime?: number;
    error?: string;
  }>;
}> => {
  const services: Array<{
    name: string;
    status: 'up' | 'down' | 'unknown';
    responseTime?: number;
    error?: string;
  }> = [];
  let startTime = Date.now();

  // Test HTMLCSStoImage API
  try {
    const apiTest = await testHTMLCSStoImageAPI();
    services.push({
      name: 'HTMLCSStoImage API',
      status: apiTest.success ? 'up' : 'down',
      error: apiTest.error
    });
  } catch (error) {
    services.push({
      name: 'HTMLCSStoImage API',
      status: 'down',
      error: error instanceof Error ? error.message : 'Unknown error'
    });
  }

  // Test Cloudflare R2
  try {
    const r2Test = await testCloudflareR2();
    services.push({
      name: 'Cloudflare R2 (banners.dealercentive.us)',
      status: r2Test.success ? 'up' : 'down',
      error: r2Test.message
    });
  } catch (error) {
    services.push({
      name: 'Cloudflare R2 (banners.dealercentive.us)',
      status: 'down',
      error: error instanceof Error ? error.message : 'Unknown error'
    });
  }

  // Test Cloudflare Worker
  try {
    const workerTest = await testCloudflareWorker();
    services.push({
      name: 'Cloudflare Worker (images.dealercentive.us)',
      status: workerTest.success ? 'up' : 'down',
      error: workerTest.message
    });
  } catch (error) {
    services.push({
      name: 'Cloudflare Worker (images.dealercentive.us)',
      status: 'down',
      error: error instanceof Error ? error.message : 'Unknown error'
    });
  }

  // Test main application
  try {
    const response = await fetch('/api/health', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      signal: AbortSignal.timeout(5000)
    });

    const responseTime = Date.now() - startTime;
    
    services.push({
      name: 'Main Application',
      status: response.ok ? 'up' : 'down',
      responseTime
    });
  } catch (error) {
    services.push({
      name: 'Main Application',
      status: 'down',
      error: error instanceof Error ? error.message : 'Unknown error'
    });
  }

  // Determine overall status
  const downServices = services.filter(s => s.status === 'down');
  const unknownServices = services.filter(s => s.status === 'unknown');
  
  let status: 'healthy' | 'degraded' | 'down';
  if (downServices.length > 0) {
    status = 'down';
  } else if (unknownServices.length > 0) {
    status = 'degraded';
  } else {
    status = 'healthy';
  }

  return { status, services };
};

// Test Cloudflare R2 connectivity
export const testCloudflareR2 = async (): Promise<{
  success: boolean;
  message: string;
  details?: any;
}> => {
  try {
    // Test if banners.dealercentive.us is accessible
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    await fetch('https://banners.dealercentive.us/', {
      method: 'HEAD',
      mode: 'no-cors',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    return {
      success: true,
      message: 'Cloudflare R2 bucket is accessible at banners.dealercentive.us',
      details: {
        domain: 'banners.dealercentive.us',
        status: 'accessible'
      }
    };
  } catch (error) {
    return {
      success: false,
      message: 'Cloudflare R2 bucket is not accessible. Please check domain configuration.',
      details: {
        domain: 'banners.dealercentive.us',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    };
  }
};

// Test Cloudflare Worker connectivity
export const testCloudflareWorker = async (): Promise<{
  success: boolean;
  message: string;
  details?: any;
}> => {
  try {
    // Test if images.dealercentive.us is accessible and returns expected 400 error
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    const response = await fetch('https://images.dealercentive.us/test', {
      method: 'GET',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    // Worker should return 400 for invalid path format
    if (response.status === 400) {
      return {
        success: true,
        message: 'Cloudflare Worker is accessible and responding correctly at images.dealercentive.us',
        details: {
          domain: 'images.dealercentive.us',
          status: 'working',
          expectedError: '400 Invalid path format'
        }
      };
    } else {
      return {
        success: false,
        message: `Cloudflare Worker responded with unexpected status: ${response.status}`,
        details: {
          domain: 'images.dealercentive.us',
          status: response.status,
          error: 'Unexpected response'
        }
      };
    }
  } catch (error) {
    return {
      success: false,
      message: 'Cloudflare Worker is not accessible. Please check worker deployment and route configuration.',
      details: {
        domain: 'images.dealercentive.us',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    };
  }
};

// Create a test HTML file for manual upload
export const createTestHTMLFile = (): void => {
  const testHtml = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Test Banner</title>
    <style>
        body {
            margin: 0;
            padding: 0;
            font-family: Arial, sans-serif;
        }
        .banner {
            width: 100%;
            height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #007bff, #00bcd4);
            color: white;
            text-align: center;
        }
        .content {
            padding: 20px;
        }
        h1 {
            font-size: 2.5em;
            margin-bottom: 10px;
        }
        p {
            font-size: 1.2em;
            margin: 0;
        }
    </style>
</head>
<body>
    <div class="banner">
        <div class="content">
            <h1>Test Banner</h1>
            <p>This is a test banner file</p>
        </div>
    </div>
</body>
</html>`;

  const blob = new Blob([testHtml], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'test-banner.html';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};